import { useEffect, useState } from 'react';
import { _notNil } from '@/littledash';
import type {
  DataTableCellDataCache,
  DataTableCellIdCoordinate,
  DataTableCellReference,
  DataTableCellUpdateEvent,
} from '../DataTable.model';
import { DataTableEvent } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';

export interface UseCellDataReturn {
  cellData: DataTableCellDataCache | undefined;
}

/**
 * Subscribe to cell data updates via CellUpdate events
 *
 * This hook manages cell data state and automatically updates when the
 * DataTableService broadcasts CellUpdate events. Uses AbortController for cleanup.
 *
 * @param idCoordinate - Cell ID coordinate (API IDs)
 * @param cellRef - Cell reference string for event filtering
 * @returns Object containing current cell data
 */
export const useCellData = (
  idCoordinate: DataTableCellIdCoordinate | undefined,
  cellRef: DataTableCellReference
): UseCellDataReturn => {
  const { dataTableService } = useDataTable();
  const [cellData, setCellData] = useState<DataTableCellDataCache | undefined>(() =>
    _notNil(idCoordinate) ? dataTableService.cellData(idCoordinate) : undefined
  );

  useEffect(() => {
    if (_notNil(idCoordinate)) {
      const abortController = new AbortController();

      // Initialize cell data
      setCellData(dataTableService.cellData(idCoordinate));

      const cellUpdateListener = (event: DataTableCellUpdateEvent) => {
        if (Object.prototype.hasOwnProperty.call(event.detail, cellRef)) {
          setCellData(event.detail[cellRef]);
        }
      };

      dataTableService.subscribe(DataTableEvent.CellUpdate, cellUpdateListener, abortController.signal);

      return () => {
        abortController.abort();
      };
    }
  }, [dataTableService, idCoordinate, cellRef]);

  return { cellData };
};
